import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElLoading, ElMessage, ElMessageBox } from 'element-plus'
import { ILoadingInstance } from 'element-plus/lib/el-loading/src/loading.type'
import { IDataType } from '@/service/common/commonType'
import qs from 'qs'
import store from '@/store'
interface LfRequestInterceptors {
  requestInterceptor?: (config: AxiosRequestConfig) => AxiosRequestConfig
  requestInterceptorCatch?: (error: any) => any
  responseInterceptor?: <T = AxiosResponse>(res: T) => T
  responseInterceptorCatch?: (error: any) => any
}

interface LfRequestConfig extends AxiosRequestConfig {
  interceptors?: LfRequestInterceptors
  showLoading?: boolean
}

const DEFAULT_LOADING = false

class LfRequest {
  instance: AxiosInstance
  interceptors?: LfRequestInterceptors
  loading?: ILoadingInstance
  showLoading?: boolean

  constructor(config: LfRequestConfig) {
    this.instance = axios.create(config)
    this.showLoading = config.showLoading ?? DEFAULT_LOADING

    this.interceptors = config.interceptors

    this.instance.interceptors.request.use(
      this.interceptors?.requestInterceptor,
      this.interceptors?.requestInterceptorCatch
    )

    this.instance.interceptors.response.use(
      this.interceptors?.responseInterceptor,
      this.interceptors?.responseInterceptorCatch
    )

    this.instance.interceptors.request.use(
      (config) => {
        if (config.method === 'get') {
          //如果是get请求，且params是数组类型如arr=[1,2]，则转换成arr=1&arr=2
          config.paramsSerializer = function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' })
          }
        }
        if (config.headers) {
          config.headers.srcUrl = window.location.hash
        } else {
          config.headers = {
            srcUrl: window.location.hash
          }
        }

        if (this.showLoading) {
          this.loading = ElLoading.service({
            lock: true,
            text: '正在请求数据...',
            background: 'rgba(0, 0, 0, 0.5)'
          })
        }
        return config
      },
      (error) => {
        return error
      }
    )

    this.instance.interceptors.response.use(
      (res: AxiosResponse<IDataType<any>>) => {
        const data = res.data
        this.loading?.close()
        if (data instanceof Blob) {
          return data
        }
        if(!data.code){
          return data
        }
        if (data.code !== 200) {
          ElMessage.error({
            message: data.message || '请求错误',
            type: 'error',
            duration: 3 * 1000
          })
          if (data.code === 401) {
            // to re-login
            ElMessageBox.confirm('登录已失效，是否重新登录', '确认登出', {
              confirmButtonText: '重新登录',
              cancelButtonText: '取消',
              type: 'warning'
            }).then(() => {
              store.dispatch('login/resetToken').then(() => {
                location.reload()
              })
            })
          }
          return Promise.reject(new Error(data.message || '请求错误'))
        }

        return data.data
      },
      (error) => {
        this.loading?.close()
        ElMessage.error({
          message: error.message,
          type: 'error',
          duration: 3 * 1000
        })
        return Promise.reject(error)
      }
    )
  }

  request<T>(config: LfRequestConfig): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (config.interceptors?.requestInterceptor) {
        config = config.interceptors.requestInterceptor(config)
      }

      if (config.showLoading !== undefined) {
        this.showLoading = config.showLoading
      }

      this.instance
        .request<any, T>(config)
        .then((res) => {
          if (config.interceptors?.responseInterceptor) {
            res = config.interceptors.responseInterceptor(res)
          }
          this.showLoading = DEFAULT_LOADING
          resolve(res)
        })
        .catch((err) => {
          this.showLoading = DEFAULT_LOADING
          reject(err)
        })
    })
  }

  get<T>(config: LfRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET' })
  }

  post<T>(config: LfRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'POST' })
  }

  delete<T>(config: LfRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE' })
  }

  put<T>(config: LfRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT' })
  }
}

export default LfRequest
